/**
 * ChatMessenger — floating popup messenger for all pages.
 *
 * UX:
 *  - Fixed bottom-right corner, always visible for logged-in users
 *  - Collapsed: chat button with unread badge
 *  - Expanded: left panel (conversation list / new-chat picker) + right panel (message thread)
 *  - Auto-expands when a new message arrives while minimised
 *  - Mark-as-read when a conversation is opened
 *  - Supports 1-on-1 and group chats
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import wsService from '../../services/websocket';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (isToday) {
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const convDisplayName = (conv, myUserId) => {
  if (conv.is_group) return conv.name || 'Group Chat';
  if (!conv.participant_ids) return 'Chat';
  const ids   = conv.participant_ids.split(',');
  const names = (conv.participant_names || '').split(',');
  const idx   = ids.findIndex((id) => id !== String(myUserId));
  return idx >= 0 ? (names[idx] || ids[idx]) : (names[0] || ids[0]);
};

const convInitials = (name) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';

/* ─── component ───────────────────────────────────────────────────────────── */

const CHAT_ADMIN_IDS = ['1147596', '1935095'];

const ChatMessenger = () => {
  const { userId } = useSelector((state) => state.auth);
  const isAdmin = CHAT_ADMIN_IDS.includes(String(userId));

  /* window state */
  const [isOpen,    setIsOpen]    = useState(false);
  const [panel,     setPanel]     = useState('list'); // 'list' | 'thread' | 'new' | 'edit'
  const [showGroupMenu, setShowGroupMenu] = useState(false);

  /* edit-group form */
  const [editGroupName,    setEditGroupName]    = useState('');
  const [editSelectedUsers, setEditSelectedUsers] = useState([]);
  const [editUserSearch,   setEditUserSearch]   = useState('');
  const [editSaving,       setEditSaving]       = useState(false);
  const [confirmDelete,    setConfirmDelete]    = useState(false);

  /* data */
  const [conversations, setConversations] = useState([]);
  const [activeConv,    setActiveConv]    = useState(null); // full conv object
  const [messages,      setMessages]      = useState([]);   // flat array for active conv
  const [users,         setUsers]         = useState([]);   // all users for new-chat

  /* new-chat form */
  const [isGroup,       setIsGroup]       = useState(false);
  const [groupName,     setGroupName]     = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch,    setUserSearch]    = useState('');

  /* quick replies */
  const [quickReplies,  setQuickReplies]  = useState([]);
  const [qrSuggestion,  setQrSuggestion]  = useState(null); // matched quick reply
  /* qr-admin form */
  const [qrEditId,      setQrEditId]      = useState(null); // null = new entry
  const [qrEditKey,     setQrEditKey]     = useState('');
  const [qrEditMsg,     setQrEditMsg]     = useState('');
  const [qrEditLabel,   setQrEditLabel]   = useState('');
  const [qrSaving,      setQrSaving]      = useState(false);
  const [qrError,       setQrError]       = useState('');

  /* input */
  const [input,         setInput]         = useState('');

  /* misc */
  const [loadingMsgs,   setLoadingMsgs]   = useState(false);
  const [unreadTotal,   setUnreadTotal]   = useState(0);

  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const activeConvRef   = useRef(null); // mirrors activeConv for use in socket callbacks
  const isOpenRef       = useRef(false); // mirrors isOpen for use in socket callbacks
  const lastMsgIdRef    = useRef(0);     // highest message id seen — used by poll fallback

  // Keep isOpenRef in sync
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  /* ── scroll-to-bottom ──────────────────────────────────────────────────── */
  const scrollBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollBottom(); }, [messages, scrollBottom]);

  /* ── fetch conversations ───────────────────────────────────────────────── */
  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/chat/conversations');
      if (res.data.success) {
        setConversations(res.data.data);
        setUnreadTotal(res.data.data.reduce((s, c) => s + (parseInt(c.unread_count) || 0), 0));
      }
    } catch (_) {}
  }, [userId]);

  /* ── fetch users for new-chat panel ───────────────────────────────────── */
  const fetchUsers = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/chat/users');
      if (res.data.success) setUsers(res.data.data);
    } catch (_) {}
  }, [userId]);

  /* ── fetch quick replies (once on mount, and after admin edits) ─────── */
  const fetchQuickReplies = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/chat/quick-replies');
      if (res.data.success) setQuickReplies(res.data.data);
    } catch (_) {}
  }, [userId]);

  useEffect(() => { fetchQuickReplies(); }, [fetchQuickReplies]);

  /* ── open a conversation ───────────────────────────────────────────────── */
  const openConversation = useCallback(async (conv) => {
    activeConvRef.current = conv;
    lastMsgIdRef.current = 0;
    setActiveConv(conv);
    setPanel('thread');
    setLoadingMsgs(true);
    try {
      const res = await api.get(`/chat/conversations/${conv.id}/messages`);
      if (res.data.success) {
        setMessages(res.data.data);
        const ids = res.data.data.map(m => m.id).filter(id => !String(id).startsWith('tmp'));
        if (ids.length) lastMsgIdRef.current = Math.max(...ids);
      }
    } catch (_) {}
    setLoadingMsgs(false);
    // mark read
    api.put(`/chat/conversations/${conv.id}/read`).catch(() => {});
    // update local unread to 0 for this conv
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c))
    );
    setUnreadTotal((prev) => Math.max(0, prev - (parseInt(conv.unread_count) || 0)));
    // focus input
    setTimeout(() => inputRef.current?.focus(), 100);
    // ensure this socket has joined the room
    const sock = wsService.socket;
    if (sock && sock.connected) sock.emit('chat:join');
  }, []);

  /* ── polling fallback — catches messages missed by socket (PM2 cluster) ─ */
  useEffect(() => {
    if (!userId) return;

    const pollInterval = setInterval(async () => {
      const conv = activeConvRef.current;
      if (!conv) {
        // No open thread — just refresh the conversation list to update previews/unread
        fetchConversations();
        return;
      }
      try {
        const res = await api.get(`/chat/conversations/${conv.id}/messages?after=${lastMsgIdRef.current}`);
        if (res.data.success && res.data.data.length > 0) {
          const newMsgs = res.data.data;
          setMessages((prev) => {
            // Deduplicate: filter out any tmp_ optimistic entries that now have real IDs
            // and any IDs already present
            const existingIds = new Set(prev.map(m => String(m.id)));
            const toAdd = newMsgs.filter(m => !existingIds.has(String(m.id)));
            if (toAdd.length === 0) return prev;
            // Remove matching optimistic messages by content+sender
            const filtered = prev.filter(m => {
              if (!String(m.id).startsWith('tmp_')) return true;
              return !toAdd.some(n => n.message === m.message && String(n.sender_id) === String(m.sender_id));
            });
            return [...filtered, ...toAdd];
          });
          const maxId = Math.max(...newMsgs.map(m => m.id));
          if (maxId > lastMsgIdRef.current) lastMsgIdRef.current = maxId;
          // Mark read
          api.put(`/chat/conversations/${conv.id}/read`).catch(() => {});
          // Refresh conversation list preview
          fetchConversations();
        }
      } catch (_) {}
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [userId, fetchConversations]);

  /* ── socket setup ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!userId) return;

    let timer;
    let attached = false;

    const playNotifSound = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      } catch (_) {}
    };

    const handleNewMessage = (msgData) => {
      const convId = String(msgData.conversation_id);
      const current = activeConvRef.current;
      const isCurrentConv = current && String(current.id) === convId;

      // Append to the currently open thread
      if (isCurrentConv) {
        setMessages((msgs) => {
          // Avoid duplicate if poll already added it
          if (msgs.some(m => String(m.id) === String(msgData.id))) return msgs;
          return [...msgs, msgData];
        });
        if (msgData.id && !String(msgData.id).startsWith('tmp_') && msgData.id > lastMsgIdRef.current) {
          lastMsgIdRef.current = msgData.id;
        }
        if (document.visibilityState === 'visible') {
          api.put(`/chat/conversations/${convId}/read`).catch(() => {});
        }
      }

      // Update conversation list preview & unread count
      setConversations((prev) =>
        prev.map((c) => {
          if (String(c.id) !== convId) return c;
          return {
            ...c,
            last_message: msgData.message,
            last_sender: msgData.sender_id,
            last_message_at: msgData.created_at,
            // only add unread if window is closed OR it's a different thread
            unread_count: (isCurrentConv && isOpenRef.current) ? 0 : (parseInt(c.unread_count) || 0) + 1,
          };
        })
      );

      // Always auto-open + sound for any incoming message
      // (whether from same or different conversation, keep window maximized)
      if (!isOpenRef.current) {
        setIsOpen(true);
      }
      // If incoming is from a different conversation than active, increment badge
      if (!isCurrentConv) {
        setUnreadTotal((t) => t + 1);
      }
      // Play notification sound
      playNotifSound();
    };

    const handleRefresh = () => {
      fetchConversations();
      const s = wsService.socket;
      if (s && s.connected) s.emit('chat:join');
    };

    const attach = () => {
      const socket = wsService.socket;
      if (!socket || !socket.connected) {
        timer = setTimeout(attach, 600);
        return;
      }
      if (attached) return;
      attached = true;

      socket.emit('chat:join');
      socket.on('chat:message', handleNewMessage);
      socket.on('chat:refresh',  handleRefresh);

      // Re-join rooms after reconnect
      socket.on('connect', () => {
        socket.emit('chat:join');
        fetchConversations();
      });
    };

    attach();
    fetchConversations();
    fetchUsers();

    return () => {
      clearTimeout(timer);
      const s = wsService.socket;
      if (s) {
        s.off('chat:message', handleNewMessage);
        s.off('chat:refresh',  handleRefresh);
      }
    };
  }, [userId, fetchConversations, fetchUsers]);

  /* ── send message ─────────────────────────────────────────────────────── */
  const sendMessage = useCallback(async () => {
    if (!input.trim() || !activeConv) return;
    const text = input.trim();
    setInput('');

    // Optimistic update — show own message immediately
    const optimistic = {
      id: `tmp_${Date.now()}`,
      conversation_id: activeConv.id,
      sender_id: String(userId),
      message: text,
      created_at: new Date().toISOString(),
    };
    setMessages((msgs) => [...msgs, optimistic]);
    setConversations((prev) =>
      prev.map((c) =>
        c.id === activeConv.id
          ? { ...c, last_message: text, last_sender: String(userId), last_message_at: optimistic.created_at }
          : c
      )
    );

    // Try socket first (fast path), fall back to REST
    const socket = wsService.socket;
    if (socket && socket.connected) {
      socket.emit('chat:send', { conversationId: activeConv.id, message: text });
    } else {
      // REST fallback — reliably saves msg and pushes to recipients
      try {
        await api.post(`/chat/conversations/${activeConv.id}/messages`, { message: text });
      } catch (_) {}
    }
  }, [input, activeConv, userId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      const trimmed = input.trim();
      const match = quickReplies.find(qr => qr.is_active && qr.shortcut === trimmed);
      if (match) {
        e.preventDefault();
        setInput(match.message);
        setQrSuggestion(null);
        return;
      }
    }
    if (e.key === 'Escape') { setQrSuggestion(null); }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /* ── open edit-group panel ───────────────────────────────────────────── */
  const openEditGroup = useCallback(() => {
    if (!activeConv) return;
    setEditGroupName(activeConv.name || '');
    // pre-select current members (excluding self)
    const ids = (activeConv.participant_ids || '').split(',').filter(id => id && id !== String(userId));
    setEditSelectedUsers(ids);
    setEditUserSearch('');
    setConfirmDelete(false);
    setShowGroupMenu(false);
    fetchUsers();
    setPanel('edit');
  }, [activeConv, userId, fetchUsers]);

  /* ── save group edits ──────────────────────────────────────────────────── */
  const saveGroupEdit = useCallback(async () => {
    if (!activeConv) return;
    setEditSaving(true);
    try {
      await api.put(`/chat/conversations/${activeConv.id}`, {
        name: editGroupName || 'Group Chat',
        participant_ids: editSelectedUsers,
      });
      await fetchConversations();
      // refresh active conv object
      const convRes = await api.get('/chat/conversations');
      if (convRes.data.success) {
        setConversations(convRes.data.data);
        const updated = convRes.data.data.find(c => c.id === activeConv.id);
        if (updated) { activeConvRef.current = updated; setActiveConv(updated); }
      }
      setPanel('thread');
    } catch (_) {}
    setEditSaving(false);
  }, [activeConv, editGroupName, editSelectedUsers, fetchConversations]);

  /* ── delete group ──────────────────────────────────────────────────────── */
  const deleteGroup = useCallback(async () => {
    if (!activeConv) return;
    try {
      await api.delete(`/chat/conversations/${activeConv.id}`);
      await fetchConversations();
      activeConvRef.current = null;
      setActiveConv(null);
      setMessages([]);
      setPanel('list');
      setConfirmDelete(false);
    } catch (_) {}
  }, [activeConv, fetchConversations]);

  /* ── create new conversation ──────────────────────────────────────────── */
  const createConversation = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    try {
      const res = await api.post('/chat/conversations', {
        name: isGroup ? (groupName || 'Group Chat') : null,
        is_group: isGroup,
        participant_ids: selectedUsers,
      });
      if (res.data.success) {
        await fetchConversations();
        // find and open the new conv
        const convRes = await api.get('/chat/conversations');
        if (convRes.data.success) {
          setConversations(convRes.data.data);
          const found = convRes.data.data.find((c) => c.id === res.data.data.id);
          if (found) openConversation(found);
        }
        // reset form
        setSelectedUsers([]);
        setGroupName('');
        setIsGroup(false);
        setUserSearch('');
      }
    } catch (_) {}
  }, [selectedUsers, isGroup, groupName, fetchConversations, openConversation]);

  /* ── toggle user selection ────────────────────────────────────────────── */
  const toggleUser = (uid) => {
    if (!isGroup) {
      // 1:1 — create immediately
      createConvWith([uid]);
      return;
    }
    setSelectedUsers((prev) =>
      prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]
    );
  };

  const createConvWith = async (ids) => {
    try {
      const res = await api.post('/chat/conversations', {
        is_group: false,
        participant_ids: ids,
      });
      if (res.data.success) {
        const convRes = await api.get('/chat/conversations');
        if (convRes.data.success) {
          setConversations(convRes.data.data);
          const found = convRes.data.data.find((c) => c.id === res.data.data.id);
          if (found) openConversation(found);
        }
        setUserSearch('');
      }
    } catch (_) {}
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  if (!userId) return null;

  const filteredUsers = users.filter(
    (u) =>
      !userSearch ||
      (u.user_name || u.user_id).toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end">

      {/* ── expanded window ───────────────────────────────────────────── */}
      {isOpen && (
        <div
          className="mb-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: 480 }}
        >
          {/* header */}
          <div
            className="flex items-center justify-between px-4 py-2.5 text-white select-none cursor-default"
            style={{ background: 'linear-gradient(90deg, #1e3c72, #2a5298)' }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {(panel === 'thread' || panel === 'new' || panel === 'edit' || panel === 'qr-admin') && (
                <button
                  onClick={() => {
                    if (panel === 'edit') { setPanel('thread'); setConfirmDelete(false); }
                    else if (panel === 'qr-admin') { setPanel('list'); setQrEditId(null); setQrEditKey(''); setQrEditMsg(''); setQrEditLabel(''); setQrError(''); }
                    else { activeConvRef.current = null; setPanel('list'); setActiveConv(null); setMessages([]); }
                    setShowGroupMenu(false);
                  }}
                  className="text-blue-200 hover:text-white text-lg leading-none mr-1 flex-shrink-0"
                >
                  ←
                </button>
              )}
              <span className="font-semibold text-sm truncate">
                {panel === 'thread' && activeConv
                  ? convDisplayName(activeConv, userId)
                  : panel === 'edit'
                  ? '✏️ Edit Group'
                  : panel === 'new'
                  ? (isGroup ? '👥 New Group Chat' : '✉️ New Message')
                  : panel === 'qr-admin'
                  ? '⚡ Quick Replies'
                  : '💬 Messages'}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {panel === 'list' && (
                <button
                  onClick={() => { setPanel('new'); fetchUsers(); }}
                  className="text-blue-200 hover:text-white text-lg leading-none"
                  title="New message"
                >
                  ✏️
                </button>
              )}
              {panel === 'list' && isAdmin && (
                <button
                  onClick={() => { setPanel('qr-admin'); setQrEditId(null); setQrEditKey(''); setQrEditMsg(''); setQrEditLabel(''); setQrError(''); }}
                  className="text-blue-200 hover:text-white text-base leading-none"
                  title="Manage quick replies"
                >
                  ⚡
                </button>
              )}
              {/* group action menu */}
              {panel === 'thread' && activeConv && activeConv.is_group && String(activeConv.created_by) === String(userId) && (
                <div className="relative">
                  <button
                    onClick={() => setShowGroupMenu(v => !v)}
                    className="text-blue-200 hover:text-white text-base leading-none px-1"
                    title="Group options"
                  >
                    ⋮
                  </button>
                  {showGroupMenu && (
                    <div className="absolute right-0 top-6 bg-white rounded-lg shadow-xl border border-gray-200 z-10 min-w-[130px] overflow-hidden">
                      <button
                        onClick={openEditGroup}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                      >
                        ✏️ Edit Group
                      </button>
                      <button
                        onClick={() => { setConfirmDelete(true); setShowGroupMenu(false); setPanel('edit'); }}
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        🗑️ Delete Group
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => { setIsOpen(false); setShowGroupMenu(false); }}
                className="text-blue-200 hover:text-white text-base leading-none"
                title="Minimise"
              >
                —
              </button>
            </div>
          </div>

          {/* ── LIST panel ─────────────────────────────────────────────── */}
          {panel === 'list' && (
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-2">
                  <span className="text-3xl">💬</span>
                  <span>No conversations yet</span>
                  <button
                    onClick={() => { setPanel('new'); fetchUsers(); }}
                    className="mt-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition"
                  >
                    Start a chat
                  </button>
                </div>
              ) : (
                conversations.map((conv) => {
                  const name  = convDisplayName(conv, userId);
                  const unread = parseInt(conv.unread_count) || 0;
                  return (
                    <button
                      key={conv.id}
                      onClick={() => openConversation(conv)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition text-left"
                    >
                      {/* avatar */}
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: conv.is_group ? '#7c3aed' : '#2563eb' }}>
                        {convInitials(name)}
                      </div>
                      {/* text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-800 text-xs truncate">{name}</span>
                          <span className="text-gray-400 text-[10px] flex-shrink-0 ml-1">
                            {fmt(conv.last_message_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 text-[11px] truncate">
                            {conv.last_message
                              ? (conv.last_sender === String(userId) ? 'You: ' : '') +
                                conv.last_message.substring(0, 35) + (conv.last_message.length > 35 ? '…' : '')
                              : 'No messages yet'}
                          </span>
                          {unread > 0 && (
                            <span className="ml-2 flex-shrink-0 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                              {unread > 9 ? '9+' : unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* ── EDIT GROUP panel ─────────────────────────────────────────── */}
          {panel === 'edit' && activeConv && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {confirmDelete ? (
                <div className="flex flex-col items-center justify-center flex-1 px-6 gap-4">
                  <div className="text-3xl">🗑️</div>
                  <p className="text-sm text-gray-700 text-center font-semibold">Delete this group?</p>
                  <p className="text-xs text-gray-500 text-center">All messages will be permanently deleted and all members will lose access.</p>
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={deleteGroup}
                      className="flex-1 py-1.5 text-xs bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* group name */}
                  <div className="px-3 pt-3 pb-1">
                    <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Group Name</label>
                    <input
                      type="text"
                      value={editGroupName}
                      onChange={(e) => setEditGroupName(e.target.value)}
                      placeholder="Group name"
                      className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                  {/* member search */}
                  <div className="px-3 pb-1">
                    <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Members</label>
                    <input
                      type="text"
                      value={editUserSearch}
                      onChange={(e) => setEditUserSearch(e.target.value)}
                      placeholder="Search users…"
                      className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                  {/* user list */}
                  <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                    {users
                      .filter(u => !editUserSearch || (u.user_name || u.user_id).toLowerCase().includes(editUserSearch.toLowerCase()))
                      .map((u) => {
                        const checked = editSelectedUsers.includes(u.user_id);
                        return (
                          <button
                            key={u.user_id}
                            onClick={() => setEditSelectedUsers(prev =>
                              prev.includes(u.user_id) ? prev.filter(id => id !== u.user_id) : [...prev, u.user_id]
                            )}
                            className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-purple-50 transition text-left ${checked ? 'bg-purple-50' : ''}`}
                          >
                            <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                              {convInitials(u.user_name || u.user_id)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-gray-800 truncate">{u.user_name || u.user_id}</div>
                              <div className="text-[10px] text-gray-500">{u.user_id}</div>
                            </div>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              checked ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                            }`}>
                              {checked && <span className="text-white text-[8px]">✓</span>}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                  {/* save */}
                  <div className="px-3 py-2 border-t border-gray-100">
                    <button
                      onClick={saveGroupEdit}
                      disabled={editSaving}
                      className="w-full py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition"
                    >
                      {editSaving ? 'Saving…' : `Save Changes (${editSelectedUsers.length} members)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── NEW CHAT panel ──────────────────────────────────────────── */}
          {panel === 'new' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* toggle 1:1 vs group */}
              <div className="flex gap-1 px-3 pt-3 pb-1">
                <button
                  onClick={() => { setIsGroup(false); setSelectedUsers([]); }}
                  className={`flex-1 py-1 text-xs rounded-lg font-semibold border transition ${
                    !isGroup ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Direct
                </button>
                <button
                  onClick={() => setIsGroup(true)}
                  className={`flex-1 py-1 text-xs rounded-lg font-semibold border transition ${
                    isGroup ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Group
                </button>
              </div>

              {isGroup && (
                <div className="px-3 pb-1">
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Group name (optional)"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                </div>
              )}

              {/* search */}
              <div className="px-3 pb-1">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users…"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>

              {/* user list */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {filteredUsers.map((u) => {
                  const checked = selectedUsers.includes(u.user_id);
                  return (
                    <button
                      key={u.user_id}
                      onClick={() => toggleUser(u.user_id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition text-left ${
                        checked ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                        {convInitials(u.user_name || u.user_id)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">
                          {u.user_name || u.user_id}
                        </div>
                        <div className="text-[10px] text-gray-500">{u.user_id}</div>
                      </div>
                      {isGroup && (
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          checked ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                        }`}>
                          {checked && <span className="text-white text-[8px]">✓</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <div className="text-center text-gray-400 text-xs py-6">No users found</div>
                )}
              </div>

              {/* group create button */}
              {isGroup && selectedUsers.length > 0 && (
                <div className="px-3 py-2 border-t border-gray-100">
                  <button
                    onClick={createConversation}
                    className="w-full py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition"
                  >
                    Create Group ({selectedUsers.length} selected)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── QUICK REPLIES ADMIN panel ────────────────────────────── */}
          {panel === 'qr-admin' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* form: add / edit */}
              <div className="px-3 pt-3 pb-2 border-b border-gray-100 space-y-1.5">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                  {qrEditId ? 'Edit Reply' : 'Add New Quick Reply'}
                </div>
                {qrError && <div className="text-[10px] text-red-500">{qrError}</div>}
                <input
                  type="text"
                  value={qrEditKey}
                  onChange={(e) => setQrEditKey(e.target.value)}
                  placeholder="Shortcut key (e.g. gm, 1, ty)"
                  maxLength={20}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <textarea
                  rows={2}
                  value={qrEditMsg}
                  onChange={(e) => setQrEditMsg(e.target.value)}
                  placeholder="Full message text…"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                />
                <input
                  type="text"
                  value={qrEditLabel}
                  onChange={(e) => setQrEditLabel(e.target.value)}
                  placeholder="Label (optional, e.g. Good Morning)"
                  maxLength={100}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <div className="flex gap-1.5">
                  <button
                    disabled={qrSaving || !qrEditKey.trim() || !qrEditMsg.trim()}
                    onClick={async () => {
                      setQrSaving(true); setQrError('');
                      try {
                        if (qrEditId) {
                          await api.put(`/chat/quick-replies/${qrEditId}`, { shortcut: qrEditKey.trim(), message: qrEditMsg.trim(), label: qrEditLabel.trim() || null });
                        } else {
                          await api.post('/chat/quick-replies', { shortcut: qrEditKey.trim(), message: qrEditMsg.trim(), label: qrEditLabel.trim() || null });
                        }
                        await fetchQuickReplies();
                        setQrEditId(null); setQrEditKey(''); setQrEditMsg(''); setQrEditLabel('');
                      } catch (err) {
                        setQrError(err.response?.data?.error || 'Error saving');
                      }
                      setQrSaving(false);
                    }}
                    className="flex-1 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition"
                  >
                    {qrSaving ? 'Saving…' : (qrEditId ? 'Save Changes' : '+ Add Reply')}
                  </button>
                  {qrEditId && (
                    <button
                      onClick={() => { setQrEditId(null); setQrEditKey(''); setQrEditMsg(''); setQrEditLabel(''); setQrError(''); }}
                      className="px-3 py-1 border border-gray-300 text-xs rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* list of existing replies */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {quickReplies.length === 0 ? (
                  <div className="text-center text-gray-400 text-xs py-6">No quick replies yet</div>
                ) : (
                  quickReplies.map((qr) => (
                    <div key={qr.id} className={`flex items-start gap-2 px-3 py-2 ${!qr.is_active ? 'opacity-50' : ''}`}>
                      <div className="flex-shrink-0 mt-0.5 w-8 h-5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">
                        {qr.shortcut}
                      </div>
                      <div className="flex-1 min-w-0">
                        {qr.label && <div className="text-[10px] font-semibold text-gray-600 truncate">{qr.label}</div>}
                        <div className="text-[10px] text-gray-500 truncate">{qr.message}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={async () => {
                            const newActive = !qr.is_active;
                            await api.put(`/chat/quick-replies/${qr.id}`, { is_active: newActive }).catch(() => {});
                            fetchQuickReplies();
                          }}
                          title={qr.is_active ? 'Disable' : 'Enable'}
                          className={`text-[10px] px-1.5 py-0.5 rounded border transition ${qr.is_active ? 'border-green-400 text-green-600 hover:bg-green-50' : 'border-gray-300 text-gray-400 hover:bg-gray-50'}`}
                        >
                          {qr.is_active ? '✓' : '○'}
                        </button>
                        <button
                          onClick={() => { setQrEditId(qr.id); setQrEditKey(qr.shortcut); setQrEditMsg(qr.message); setQrEditLabel(qr.label || ''); setQrError(''); }}
                          title="Edit"
                          className="text-[10px] px-1.5 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete shortcut "${qr.shortcut}"?`)) return;
                            await api.delete(`/chat/quick-replies/${qr.id}`).catch(() => {});
                            fetchQuickReplies();
                          }}
                          title="Delete"
                          className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-500 hover:bg-red-50 transition"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── THREAD panel ────────────────────────────────────────────── */}
          {panel === 'thread' && activeConv && (
            <div
              className="flex-1 flex flex-col overflow-hidden"
              data-active-conv={activeConv.id}
            >
              {/* messages */}
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                {loadingMsgs ? (
                  <div className="text-center text-gray-400 text-xs py-6">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-400 text-xs py-6">
                    No messages yet. Say hello! 👋
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMine = String(msg.sender_id) === String(userId);
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[72%] px-3 py-1.5 rounded-2xl text-xs leading-snug shadow-sm ${
                          isMine
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}>
                          {!isMine && activeConv.is_group && (
                            <div className="font-semibold text-[10px] text-blue-600 mb-0.5">
                              {msg.sender_name || msg.sender_id}
                            </div>
                          )}
                          <div className="break-words whitespace-pre-wrap">{msg.message}</div>
                          <div className={`text-[9px] mt-0.5 text-right ${isMine ? 'text-blue-200' : 'text-gray-400'}`}>
                            {fmt(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* quick-reply suggestion hint */}
              {qrSuggestion && (
                <div className="mx-3 mb-0 pb-1 flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-[10px] text-blue-700 leading-snug">
                    <span className="font-semibold text-blue-500">↹ Tab</span>
                    {' → '}
                    <span className="text-blue-800">{qrSuggestion.message.length > 55 ? qrSuggestion.message.substring(0, 55) + '…' : qrSuggestion.message}</span>
                  </div>
                  <button onClick={() => setQrSuggestion(null)} className="text-gray-400 hover:text-gray-600 text-xs flex-shrink-0">✕</button>
                </div>
              )}

              {/* input */}
              <div className="flex items-end gap-1.5 px-3 py-2 border-t border-gray-100">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInput(val);
                    const trimmed = val.trim();
                    const match = quickReplies.find(qr => qr.is_active && qr.shortcut === trimmed);
                    setQrSuggestion(match || null);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message… (shortcut + Tab to expand)"
                  className="flex-1 resize-none px-3 py-1.5 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-400 max-h-20 overflow-y-auto"
                  style={{ lineHeight: '1.4' }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim()}
                  className="flex-shrink-0 w-8 h-8 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" style={{ marginLeft: 1 }}>
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── chat toggle button ─────────────────────────────────────────── */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-13 h-13 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 relative"
        style={{
          width: 52,
          height: 52,
          background: 'linear-gradient(135deg, #1e3c72, #2a5298)',
          boxShadow: '0 4px 18px rgba(30,60,114,0.45)',
        }}
        title={isOpen ? 'Minimise chat' : 'Open chat'}
      >
        {/* icon */}
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
          </svg>
        )}

        {/* unread badge */}
        {!isOpen && unreadTotal > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5"
          >
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatMessenger;
